import { useState, useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, Plus, Users } from "lucide-react";
import { useContacts } from "@/hooks/use-contacts";
import { DataTable } from "@/components/shared/data-table";
import { ContactForm } from "@/components/contacts/contact-form";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatDateTime, formatPhone } from "@/lib/format";
import type { Contact } from "@/types/contact";
import { toast } from "sonner";

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function ContactsPage() {
  const { contacts, loading, createContact, updateContact, deleteContact } =
    useContacts();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [deleting, setDeleting] = useState<Contact | null>(null);

  const handleCreate = async (data: Partial<Contact>) => {
    try {
      await createContact(data);
      toast.success("Contato criado");
    } catch {
      toast.error("Erro ao criar contato");
      throw new Error("create failed");
    }
  };

  const handleUpdate = async (data: Partial<Contact>) => {
    if (!editing) return;
    try {
      await updateContact(editing.id, data);
      toast.success("Contato atualizado");
    } catch {
      toast.error("Erro ao atualizar contato");
      throw new Error("update failed");
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    const id = deleting.id;
    try {
      await deleteContact(id);
      toast.success("Contato excluido");
    } catch {
      toast.error("Erro ao excluir contato");
    }
  };

  const columns: ColumnDef<Contact>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Contato",
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <Avatar size="default">
              <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
                {getInitials(row.original.name)}
              </AvatarFallback>
            </Avatar>
            <span className="font-medium">{row.original.name}</span>
          </div>
        ),
        filterFn: (row, _columnId, filterValue) => {
          const name = row.original.name?.toLowerCase() || "";
          const phone = row.original.phone_number?.toLowerCase() || "";
          const search = filterValue.toLowerCase();
          return name.includes(search) || phone.includes(search);
        },
      },
      {
        accessorKey: "phone_number",
        header: "Telefone",
        cell: ({ row }) => {
          const phone = row.original.phone_number;
          if (!phone) return <span className="text-muted-foreground">-</span>;
          return (
            <span className="font-mono text-sm">{formatPhone(phone)}</span>
          );
        },
      },
      {
        accessorKey: "identifier",
        header: "Identificador",
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm">
            {row.original.identifier || "-"}
          </span>
        ),
      },
      {
        accessorKey: "datetime_alt",
        header: "Ultima atividade",
        cell: ({ row }) => {
          const date = row.original.datetime_alt || row.original.datetime_add;
          return (
            <span className="text-muted-foreground text-sm">
              {formatDateTime(date)}
            </span>
          );
        },
      },
      {
        id: "actions",
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onSelect={() => {
                  setTimeout(() => {
                    setEditing(row.original);
                    setFormOpen(true);
                  }, 0);
                }}
              >
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive"
                onSelect={() => setDeleting(row.original)}
              >
                Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [],
  );

  if (loading) {
    return (
      <div className="h-full overflow-auto space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-5 w-10 rounded-full" />
          </div>
          <Skeleton className="h-9 w-32" />
        </div>
        <Skeleton className="h-10 w-80" />
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 py-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-28" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold">Contatos</h1>
          <Badge variant="secondary" className="gap-1">
            <Users className="h-3 w-3" />
            {contacts.length}
          </Badge>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          Novo contato
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={contacts}
        searchPlaceholder="Buscar por nome ou telefone..."
        searchColumn="name"
      />

      <ContactForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSubmit={editing ? handleUpdate : handleCreate}
        contact={editing}
      />

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
